import { useLiveQuery } from 'dexie-react-hooks';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { firstOfMonth, isValidYearMonth, lastOfMonth, monthOf, type ExpenseRecord } from '@dairy/shared';
import { useSession } from '@/auth/useSession';
import { Badge, Card, CardTitle, EmptyState, Field, Input, PageHeader, Select, buttonVariants } from '@/components/ui';
import { db } from '@/db/db';
import { useAnimals, useToday } from '@/db/hooks';
import { useFormat } from '@/lib/format';
import { CategorySelect, categoryName, useCategories } from './CategorySelect';

/** Screen 18 — Expenses list (EX-5): month, category and animal filters; totals by category; managers see their own. */
export function ExpensesPage() {
  const { t } = useTranslation();
  const f = useFormat();
  const locale = useSession().profile?.locale ?? 'am';
  const today = useToday();
  const [month, setMonth] = useState(monthOf(today));
  const [categoryId, setCategoryId] = useState('');
  const [animalId, setAnimalId] = useState('');
  const cats = useCategories();
  const animals = useAnimals() ?? [];
  const valid = isValidYearMonth(month);
  const expenses =
    useLiveQuery(
      () => (valid ? db.expenses.where('date').between(firstOfMonth(month), lastOfMonth(month), true, true).toArray() : Promise.resolve([] as ExpenseRecord[])),
      [month, valid],
    ) ?? [];
  const list = expenses
    .filter((e) => (!categoryId || e.categoryId === categoryId) && (!animalId || e.animalId === animalId))
    .sort((a, b) => (a.date < b.date ? 1 : -1));
  const byCat = new Map<string, number>();
  for (const e of list) byCat.set(e.categoryId, (byCat.get(e.categoryId) ?? 0) + e.amountSantim);
  const total = list.reduce((a, e) => a + e.amountSantim, 0);
  const catById = new Map(cats.map((c) => [c.id, c]));
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('expenses.title')}
        actions={
          <Link to="/expenses/new" className={buttonVariants({ size: 'sm' })}>
            <Plus className="h-4 w-4" /> {t('expenses.add')}
          </Link>
        }
      />
      <Card className="grid gap-3 sm:grid-cols-3">
        <Field label={t('common.month')}>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </Field>
        <Field label={t('expenses.category')}>
          <CategorySelect value={categoryId} onChange={setCategoryId} />
        </Field>
        <Field label={t('expenses.animal')}>
          <Select value={animalId} onChange={(e) => setAnimalId(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {animals.map((a) => (
              <option key={a.id} value={a.id}>
                {a.tag}
              </option>
            ))}
          </Select>
        </Field>
      </Card>
      <Card>
        <CardTitle>{t('expenses.totalsByCategory')}</CardTitle>
        <ul className="divide-y divide-stone-100">
          {[...byCat.entries()]
            .sort((a, b) => b[1] - a[1])
            .map(([id, sum]) => (
              <li key={id} className="flex justify-between py-2">
                <span>{categoryName(catById.get(id), locale)}</span>
                <span className="tabular-nums">{f.birr(sum)}</span>
              </li>
            ))}
        </ul>
        <div className="mt-2 flex justify-between border-t border-stone-200 pt-2 font-bold">
          <span>{t('common.total')}</span>
          <span className="tabular-nums">{f.birr(total)}</span>
        </div>
      </Card>
      {list.length === 0 ? <EmptyState>{t('expenses.empty')}</EmptyState> : null}
      <ul className="space-y-2">
        {list.map((e) => (
          <li key={e.id}>
            <Link to={`/expenses/${e.id}/edit`}>
              <Card className="flex items-center gap-3 p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{categoryName(catById.get(e.categoryId), locale)}</div>
                  <div className="text-sm text-stone-600">
                    {f.short(e.date)}
                    {e.description ? ` · ${e.description}` : ''}
                    {e.coversMonths > 1 ? ` · ${t('expenses.coversN', { count: e.coversMonths })}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold tabular-nums">{f.birr(e.amountSantim)}</div>
                  {e.sourceEventId ? <Badge>{t('expenses.fromEvent')}</Badge> : null}
                  {e.recurringExpenseId ? <Badge tone="blue">{t('expenses.recurringBadge')}</Badge> : null}
                </div>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
